
import React, { useState, useMemo } from 'react';
import { User, UserRole, ResidentUnit, UnitType, UnitHistoryEntry } from '../types';
import { MOCK_RESIDENTS } from '../mockData';
import { 
  Users, 
  Search, 
  Home, 
  Briefcase, 
  User as UserIcon, 
  Phone, 
  Mail, 
  Info, 
  Edit,
  Trash2,
  History,
  CheckCircle2,
  XCircle,
  Key,
  ShieldCheck,
  Plus,
  X,
  Clock,
  AlertTriangle,
  UserCheck,
  Download,
  Layers,
  LayoutList,
  ChevronDown,
  ChevronUp,
  Bell,
  CheckCircle,
  Smartphone,
  AtSign,
  MessageSquare
} from 'lucide-react';

interface ResidentsProps {
  user: User;
  onMessageRequest?: (userId: string) => void;
}

const Residents: React.FC<ResidentsProps> = ({ user, onMessageRequest }) => {
  const [residents, setResidents] = useState<ResidentUnit[]>(MOCK_RESIDENTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [groupBy, setGroupBy] = useState<'NONE' | 'FLOOR'>('NONE');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [notificationPrompt, setNotificationPrompt] = useState<{show: boolean, contact: string, type: 'EMAIL' | 'PHONE'} | null>(null);
  
  // Form/Active State
  const [editingUnit, setEditingUnit] = useState<ResidentUnit | null>(null);
  const [viewingHistory, setViewingHistory] = useState<ResidentUnit | null>(null);
  const [unitToDelete, setUnitToDelete] = useState<ResidentUnit | null>(null);
  
  const [formState, setFormState] = useState<Partial<ResidentUnit>>({
    unitNumber: '',
    floor: '',
    type: UnitType.RESIDENTIAL,
    isOccupied: false,
    isRented: false,
    tenantName: '',
    tenantEmail: '',
    tenantPhone: '',
    ownerName: '',
    ownerContact: ''
  });

  const isAdmin = user.role === UserRole.PRESIDENT || user.role === UserRole.DEPUTY || user.role === UserRole.SUPER_ADMIN;
  const isSuperAdmin = user.role === UserRole.SUPER_ADMIN;

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-white rounded-3xl border border-slate-200">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">Пристап одбиен</h2>
        <p className="text-slate-500">Само раководството може да пристапи до регистарот.</p>
      </div>
    );
  }

  const filteredResidents = useMemo(() => {
    return residents.filter(r => {
      const matchesSearch = 
        r.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) || 
        r.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.unitNumber.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = typeFilter === 'ALL' || r.type === typeFilter;
      const matchesStatus = statusFilter === 'ALL' || 
                           (statusFilter === 'OCCUPIED' && r.isOccupied) || 
                           (statusFilter === 'VACANT' && !r.isOccupied) ||
                           (statusFilter === 'RENTED' && r.isRented);
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [residents, searchQuery, typeFilter, statusFilter]);

  const groupedResidents = useMemo(() => {
    if (groupBy === 'NONE') return { 'Сите': filteredResidents };
    const groups: Record<string, ResidentUnit[]> = {};
    filteredResidents.forEach(r => {
      const key = `Кат ${r.floor || 'Приземје'}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    return groups;
  }, [filteredResidents, groupBy]);

  const toggleGroup = (group: string) => {
    const next = new Set(expandedGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    setExpandedGroups(next);
  };

  const handleOpenForm = (unit?: ResidentUnit) => {
    if (unit) {
      setEditingUnit(unit);
      setFormState(unit);
    } else {
      setEditingUnit(null);
      setFormState({
        unitNumber: '',
        floor: '',
        type: UnitType.RESIDENTIAL,
        isOccupied: false,
        isRented: false,
        tenantName: '',
        tenantEmail: '',
        tenantPhone: '',
        ownerName: '',
        ownerContact: ''
      });
    }
    setIsFormOpen(true);
  };

  const exportToCSV = () => {
    if (!isSuperAdmin) return;
    const headers = ["Број на единица", "Кат", "Тип", "Статус", "Кирија", "Станар", "Е-пошта", "Телефон", "Сопственик"];
    const rows = residents.map(r => [
      r.unitNumber,
      r.floor,
      r.type,
      r.isOccupied ? "Вселен" : "Празен",
      r.isRented ? "Да" : "Не",
      r.tenantName,
      r.tenantEmail,
      r.tenantPhone,
      r.ownerName
    ]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `civichome_residents_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveUnit = () => {
    const timestamp = new Date().toISOString();
    
    // Check for contact changes to prompt notification
    if (editingUnit) {
      const emailChanged = editingUnit.tenantEmail !== formState.tenantEmail;
      const phoneChanged = editingUnit.tenantPhone !== formState.tenantPhone;
      
      if ((emailChanged && formState.tenantEmail) || (phoneChanged && formState.tenantPhone)) {
        setNotificationPrompt({
          show: true,
          contact: emailChanged ? formState.tenantEmail! : formState.tenantPhone!,
          type: emailChanged ? 'EMAIL' : 'PHONE'
        });
      }
    }

    const historyEntry: UnitHistoryEntry = {
      id: `h-${Date.now()}`,
      timestamp,
      changeType: editingUnit ? 'STATUS_CHANGE' : 'CREATION',
      description: editingUnit ? 'Промена на информации за единицата.' : 'Нова единица е додадена во системот.',
      performedBy: user.name
    };

    if (editingUnit) {
      const specificHistory: UnitHistoryEntry[] = [];
      if (editingUnit.tenantName !== formState.tenantName) {
        specificHistory.push({
          id: `h-t-${Date.now()}`,
          timestamp,
          changeType: 'TENANT_CHANGE',
          description: `Промена на корисник: од "${editingUnit.tenantName}" во "${formState.tenantName}"`,
          performedBy: user.name
        });
      }
      if (editingUnit.ownerName !== formState.ownerName) {
        specificHistory.push({
          id: `h-o-${Date.now()}`,
          timestamp,
          changeType: 'OWNER_CHANGE',
          description: `Промена на сопственик: од "${editingUnit.ownerName}" во "${formState.ownerName}"`,
          performedBy: user.name
        });
      }

      setResidents(prev => prev.map(r => r.id === editingUnit.id ? { 
        ...formState, 
        id: r.id, 
        history: [...specificHistory, ...r.history] 
      } as ResidentUnit : r));
    } else {
      const newUnit: ResidentUnit = {
        ...formState,
        id: `r-${Date.now()}`,
        history: [historyEntry]
      } as ResidentUnit;
      setResidents([newUnit, ...residents]);
    }
    setIsFormOpen(false);
  };

  const handleDeleteUnit = () => {
    if (unitToDelete) {
      setResidents(prev => prev.filter(r => r.id !== unitToDelete.id));
      setIsConfirmDeleteOpen(false);
      setUnitToDelete(null);
    }
  };

  const getHistoryIcon = (type: UnitHistoryEntry['changeType']) => {
    switch (type) {
      case 'CREATION': return <Plus className="w-4 h-4 text-emerald-500" />;
      case 'TENANT_CHANGE': return <UserIcon className="w-4 h-4 text-indigo-500" />;
      case 'OWNER_CHANGE': return <Key className="w-4 h-4 text-amber-500" />;
      case 'STATUS_CHANGE': return <Info className="w-4 h-4 text-slate-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Регистар на станари</h1>
          <p className="text-slate-500">Управувајте со заедницата и следете ја историјата на објектот.</p>
        </div>
        <div className="flex items-center space-x-2">
          {isSuperAdmin && (
            <button 
              onClick={exportToCSV}
              className="bg-slate-100 text-slate-700 px-4 py-2.5 rounded-xl font-bold hover:bg-slate-200 transition-all flex items-center border border-slate-200"
              title="Извези CSV"
            >
              <Download className="w-4 h-4 mr-2" /> Извези
            </button>
          )}
          <button 
            onClick={() => handleOpenForm()}
            className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" /> Додај нова единица
          </button>
        </div>
      </header>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Вкупно единици', value: residents.length, color: 'slate', icon: Home },
          { label: 'Вселени', value: residents.filter(r => r.isOccupied).length, color: 'emerald', icon: UserCheck },
          { label: 'Деловни', value: residents.filter(r => r.type === UnitType.BUSINESS).length, color: 'indigo', icon: Briefcase },
          { label: 'Издадени', value: residents.filter(r => r.isRented).length, color: 'amber', icon: Key }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">{stat.label}</span>
              <span className={`text-2xl font-black text-${stat.color}-600`}>{stat.value}</span>
            </div>
            <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-500`}>
              <stat.icon className="w-5 h-5" />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
        {/* Filters Area */}
        <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Пребарај по име, стан, сопственик..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
            />
          </div>
          
          <div className="flex items-center space-x-2 bg-white p-1 rounded-xl border border-slate-200">
             <button 
              onClick={() => setGroupBy('NONE')}
              className={`p-1.5 rounded-lg transition-all ${groupBy === 'NONE' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title="Листа"
             >
               <LayoutList className="w-4 h-4" />
             </button>
             <button 
              onClick={() => setGroupBy('FLOOR')}
              className={`p-1.5 rounded-lg transition-all ${groupBy === 'FLOOR' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title="Групирај по кат"
             >
               <Layers className="w-4 h-4" />
             </button>
          </div>

          <div className="flex space-x-2">
            <select 
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 outline-none"
            >
              <option value="ALL">Сите типови</option>
              <option value={UnitType.RESIDENTIAL}>Станбени</option>
              <option value={UnitType.BUSINESS}>Деловни</option>
            </select>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 outline-none"
            >
              <option value="ALL">Сите статуси</option>
              <option value="OCCUPIED">Вселени</option>
              <option value="VACANT">Празни</option>
              <option value="RENTED">Под кирија</option>
            </select>
          </div>
        </div>

        {/* Units Content */}
        <div className="overflow-x-auto">
          {(Object.entries(groupedResidents) as [string, ResidentUnit[]][]).map(([groupName, groupItems]) => {
            const isExpanded = groupBy === 'NONE' || expandedGroups.has(groupName);
            return (
              <div key={groupName} className="border-b last:border-b-0 border-slate-100">
                {groupBy !== 'NONE' && (
                  <button 
                    onClick={() => toggleGroup(groupName)}
                    className="w-full flex items-center justify-between px-6 py-4 bg-slate-50/30 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      <span className="font-bold text-slate-900">{groupName}</span>
                      <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[10px] font-bold">
                        {groupItems.length} единици
                      </span>
                    </div>
                  </button>
                )}
                
                {isExpanded && (
                  <table className="w-full text-left">
                    {groupBy === 'NONE' && (
                      <thead>
                        <tr className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b">
                          <th className="px-6 py-4">Единица</th>
                          <th className="px-6 py-4">Корисник / Сопственик</th>
                          <th className="px-6 py-4">Статус</th>
                          <th className="px-6 py-4 text-right">Акции</th>
                        </tr>
                      </thead>
                    )}
                    <tbody className="divide-y divide-slate-50">
                      {groupItems.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-6 py-5">
                            <div className="flex items-center space-x-3">
                              <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-black ${
                                r.type === UnitType.BUSINESS ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {r.unitNumber}
                              </div>
                              <div>
                                <span className="block text-xs font-bold text-slate-900">
                                  {r.type === UnitType.BUSINESS ? 'Деловен простор' : 'Станбена единица'}
                                </span>
                                <div className="flex items-center text-[10px] font-medium text-slate-400 mt-0.5">
                                   {r.type === UnitType.BUSINESS ? <Briefcase className="w-3 h-3 mr-1" /> : <Home className="w-3 h-3 mr-1" />}
                                   <span>{r.unitNumber} {r.floor && `• Кат ${r.floor}`}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <UserCheck className="w-3.5 h-3.5 text-indigo-500" />
                                <span className="text-sm font-bold text-slate-800">{r.tenantName}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <ShieldCheck className="w-3 h-3" />
                                <span>Сопственик: {r.ownerName}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex flex-wrap gap-1.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                r.isOccupied ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-100'
                              }`}>
                                {r.isOccupied ? 'Вселен' : 'Празен'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                r.isRented ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                              }`}>
                                {r.isRented ? 'Под кирија' : 'Сопственик користи'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => onMessageRequest?.(r.id)}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                title="Испрати порака"
                              >
                                <MessageSquare className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => { setViewingHistory(r); setIsHistoryOpen(true); }}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                title="Историја"
                              >
                                <History className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleOpenForm(r)}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                title="Уреди"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => { setUnitToDelete(r); setIsConfirmDeleteOpen(true); }}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Избриши"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Notification Prompt Modal */}
      {notificationPrompt?.show && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setNotificationPrompt(null)} />
          <div className="relative bg-white p-8 rounded-[2rem] shadow-2xl max-w-sm w-full animate-in zoom-in duration-200 border border-indigo-100">
             <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
               <Bell className="w-8 h-8" />
             </div>
             <h3 className="text-xl font-black text-center text-slate-900 mb-2">Извести го корисникот?</h3>
             <p className="text-slate-500 text-center text-sm mb-8 leading-relaxed">
               Дали сакате да испратите автоматска нотификација до <span className="font-bold text-indigo-600">{notificationPrompt.contact}</span> со нивните нови детали?
             </p>
             <div className="grid grid-cols-2 gap-4">
                <button 
                  onClick={() => setNotificationPrompt(null)}
                  className="py-3 font-bold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200"
                >
                  Не
                </button>
                <button 
                  onClick={() => {
                    // Mock send notification
                    setNotificationPrompt(null);
                  }}
                  className="py-3 font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 flex items-center justify-center"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Испрати
                </button>
             </div>
          </div>
        </div>
      )}

      {/* CRUD Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsFormOpen(false)} />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col p-8 animate-in slide-in-from-right duration-300">
            <header className="flex justify-between items-center mb-8 border-b pb-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{editingUnit ? 'Уреди единица' : 'Додај единица'}</h2>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Основни информации</p>
              </div>
              <button onClick={() => setIsFormOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X /></button>
            </header>

            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5 col-span-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Единица</label>
                  <input 
                    value={formState.unitNumber}
                    onChange={e => setFormState({...formState, unitNumber: e.target.value})}
                    placeholder="пр. 4Б"
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Кат</label>
                  <input 
                    value={formState.floor}
                    onChange={e => setFormState({...formState, floor: e.target.value})}
                    placeholder="пр. 4"
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Тип</label>
                  <select 
                    value={formState.type}
                    onChange={e => setFormState({...formState, type: e.target.value as UnitType})}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none"
                  >
                    <option value={UnitType.RESIDENTIAL}>Станбена</option>
                    <option value={UnitType.BUSINESS}>Деловна</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Сопственик (Име и презиме)</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    value={formState.ownerName}
                    onChange={e => setFormState({...formState, ownerName: e.target.value})}
                    className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-1.5 pt-4 border-t">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Корисник / Тековен станар</label>
                <div className="relative">
                  <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    value={formState.tenantName}
                    onChange={e => setFormState({...formState, tenantName: e.target.value})}
                    className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Телефон</label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      value={formState.tenantPhone}
                      onChange={e => setFormState({...formState, tenantPhone: e.target.value})}
                      className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Е-пошта</label>
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      value={formState.tenantEmail}
                      onChange={e => setFormState({...formState, tenantEmail: e.target.value})}
                      className="w-full p-3 pl-10 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-4 shadow-inner">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Статус на единицата</h4>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Вселена / Активна</span>
                  <button 
                    onClick={() => setFormState({...formState, isOccupied: !formState.isOccupied})}
                    className={`w-12 h-6 rounded-full transition-all relative ${formState.isOccupied ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formState.isOccupied ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Издадена под кирија</span>
                  <button 
                    onClick={() => setFormState({...formState, isRented: !formState.isRented})}
                    className={`w-12 h-6 rounded-full transition-all relative ${formState.isRented ? 'bg-amber-500' : 'bg-slate-300'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formState.isRented ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
              </div>
            </div>

            <footer className="pt-8 border-t mt-auto">
               <button 
                onClick={handleSaveUnit}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-[0.98]"
               >
                 {editingUnit ? 'Зачувај промени' : 'Додај во регистар'}
               </button>
            </footer>
          </div>
        </div>
      )}

      {/* History Modal */}
      {isHistoryOpen && viewingHistory && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsHistoryOpen(false)} />
          <div className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-indigo-100">
            <header className="p-8 bg-indigo-600 text-white flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-black">Хронологија на единицата</h3>
                <p className="text-indigo-100 font-bold text-xs uppercase tracking-widest mt-1">
                  {viewingHistory.type === UnitType.BUSINESS ? 'Деловен простор' : 'Стан'} {viewingHistory.unitNumber}
                </p>
              </div>
              <button onClick={() => setIsHistoryOpen(false)} className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"><X className="w-5 h-5" /></button>
            </header>
            
            <div className="p-8 max-h-[60vh] overflow-y-auto bg-slate-50/30">
              {viewingHistory.history.length > 0 ? (
                <div className="relative border-l-2 border-indigo-200 ml-4 pl-8 space-y-8 pb-4">
                  {viewingHistory.history.map((entry) => (
                    <div key={entry.id} className="relative group/item">
                      <div className={`absolute -left-[45px] top-1 w-8 h-8 bg-white border-2 rounded-full flex items-center justify-center shadow-sm z-10 transition-transform group-hover/item:scale-110 ${
                        entry.changeType === 'CREATION' ? 'border-emerald-400' :
                        entry.changeType === 'TENANT_CHANGE' ? 'border-indigo-400' :
                        entry.changeType === 'OWNER_CHANGE' ? 'border-amber-400' : 'border-slate-300'
                      }`}>
                        {getHistoryIcon(entry.changeType)}
                      </div>
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 transition-all group-hover/item:border-indigo-100 group-hover/item:shadow-md">
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
                            entry.changeType === 'CREATION' ? 'bg-emerald-50 text-emerald-600' :
                            entry.changeType === 'TENANT_CHANGE' ? 'bg-indigo-50 text-indigo-600' :
                            entry.changeType === 'OWNER_CHANGE' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {entry.changeType === 'TENANT_CHANGE' ? 'Замена на станар' : 
                             entry.changeType === 'OWNER_CHANGE' ? 'Замена на сопственик' : 
                             entry.changeType === 'CREATION' ? 'Креирање' : 'Промена на статус'}
                          </span>
                          <span className="text-[10px] font-bold text-slate-400 flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(entry.timestamp).toLocaleString('mk-MK', {day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-slate-700 leading-relaxed mb-3">{entry.description}</p>
                        <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                          <div className="flex items-center text-[10px] text-slate-400 font-bold uppercase">
                            <ShieldCheck className="w-3 h-3 mr-1 text-emerald-400" /> 
                            <span className="opacity-70">Извршено од:</span> <span className="text-slate-600 ml-1">{entry.performedBy}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                   <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                     <History className="w-8 h-8" />
                   </div>
                   <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Нема историја</p>
                </div>
              )}
            </div>
            
            <footer className="p-6 bg-white border-t text-center">
              <button 
                onClick={() => setIsHistoryOpen(false)} 
                className="px-12 py-3 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-lg active:scale-95"
              >
                Разбрав
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isConfirmDeleteOpen && unitToDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsConfirmDeleteOpen(false)} />
          <div className="relative bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full animate-in zoom-in duration-200">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-center text-slate-900 mb-2">Бришење на единица</h3>
            <p className="text-slate-500 text-center text-sm mb-8 leading-relaxed">
              Дали сте сигурни дека сакате да ја избришете единицата <span className="font-black text-slate-900">{unitToDelete.unitNumber}</span>? Оваа акција е неповратна.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setIsConfirmDeleteOpen(false)}
                className="py-3 font-bold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
              >
                Откажи
              </button>
              <button 
                onClick={handleDeleteUnit}
                className="py-3 font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-100"
              >
                Избриши
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Residents;
